/**
 * Auth-aware fetch surface for the widget (P1-057).
 *
 * Implements the browser side of the design in
 * `planning/AUTHENTICATION.md`. The Sitecore CD broker (P1-039) is
 * documented but not yet built; this module is wired against the
 * documented contract and is exercised end-to-end against a real
 * broker as a follow-up post-Track-B.
 *
 * The single entry point — {@link createAuthClient} — returns a
 * helper that:
 *
 *   1. Lazily fetches a JWT from the configured `tokenEndpoint` on
 *      first need (never on widget load).
 *   2. Caches it in memory only — `localStorage` / `sessionStorage`
 *      / `IndexedDB` and any widget-set cookies are explicitly
 *      forbidden by AUTHENTICATION.md §13 ("token never persisted to
 *      storage, never logged"). The token does not leave this
 *      closure in human-readable form.
 *   3. Pre-emptively refreshes before expiry (see the
 *      `lifetimeMs - lead` comment inside `fetchTokenInternal`).
 *   4. Reactively refreshes on a 401 from the downstream API: drop
 *      the cached token, fetch a fresh one, retry the original
 *      request exactly once. A second 401 surfaces as
 *      {@link AuthError} with `kind: 'unauthorized'`.
 *   5. Attaches `Authorization: Bearer <jwt>` via a freshly-cloned
 *      `Headers` instance — caller-provided headers/objects are
 *      never mutated in place.
 *   6. Coalesces concurrent token fetches: two simultaneous
 *      `authedFetch` calls before the first token resolves share
 *      one in-flight broker request.
 *   7. Falls back to **pass-through** mode when `tokenEndpoint` is
 *      empty/unset — no broker fetch, no `Authorization` header.
 *      This is the local-dev / DevBypass path: the local Assistant
 *      API does not require a Bearer (`Authentication:DevBypass`),
 *      so an unconfigured `token-endpoint` deliberately MUST NOT
 *      cause auth errors.
 *
 * Failures surface as a typed {@link AuthError} **and** a bubbling,
 * composed `mwi-assistant-auth-error` {@link CustomEvent} on the
 * provided host element so embedding pages can react. Per the
 * security boundary in AUTHENTICATION.md §13, the event detail is
 * deliberately minimal (`{ kind, status? }`) — it carries no token
 * and no PII.
 *
 * Future widget-side network code (P1-058 SSE; P3-022 feedback)
 * consumes this surface; today the only real consumer is the
 * harness probe panel in `index.html`.
 */

import { resolveTokenEndpoint } from './api-client.js';

/**
 * Wire-format of the broker response.
 *
 * Defined as a typed boundary deliberately — per the BACKLOG.md
 * §1.6 hard rules, no `any` is allowed at this seam. A response
 * that does not satisfy {@link isValidBrokerResponse} is treated
 * as a `broker-rejected` failure.
 *
 * Source: AUTHENTICATION.md §10 ("returns the resulting JWT to the
 * browser as JSON: `{ access_token: '...', expires_in: 900,
 * token_type: 'Bearer' }`").
 */
export interface BrokerTokenResponse {
  readonly access_token: string;
  readonly expires_in: number;
  readonly token_type: string;
}

/**
 * Discriminator for the structured failure surface.
 *
 *  - `broker-unreachable` — the broker fetch threw (network, DNS,
 *    CORS, TLS, abort). No HTTP status to attach.
 *  - `broker-rejected`    — the broker returned a non-2xx response
 *    or malformed JSON. Status is attached.
 *  - `unauthorized`       — the downstream API returned 401 even
 *    after a fresh token was fetched. Status is attached (always
 *    401 today; the type leaves room for future variants).
 */
export type AuthErrorKind =
  | 'broker-unreachable'
  | 'broker-rejected'
  | 'unauthorized';

/**
 * Event name for the bubbling/composed {@link CustomEvent} dispatched
 * on the auth client's host element whenever a failure surfaces.
 * Detail shape is {@link MwiAssistantAuthErrorEventDetail}.
 *
 * Hosts wire this once on the `<mwi-assistant>` element (or any
 * ancestor — `composed: true` lets it cross the shadow boundary)
 * and react however they like (analytics, sign-in nudge, etc.).
 */
export const MWI_ASSISTANT_AUTH_ERROR_EVENT = 'mwi-assistant-auth-error';

/**
 * Detail payload of the {@link MWI_ASSISTANT_AUTH_ERROR_EVENT}.
 *
 * **Security boundary (AUTHENTICATION.md §13):** intentionally
 * carries the kind and the optional HTTP status only. The token,
 * the request URL, the broker URL, and any PII (user id / user account id /
 * email claims) are NEVER included. Hosts that want richer
 * diagnostics must instrument server-side.
 */
export interface MwiAssistantAuthErrorEventDetail {
  readonly kind: AuthErrorKind;
  readonly status?: number;
}

/**
 * Structured failure thrown by {@link AuthClient.authedFetch} on
 * unrecoverable auth issues.
 *
 * Use `instanceof AuthError` to discriminate from generic
 * `TypeError`s that may bubble out of `fetch` itself (e.g. CORS
 * blocked the response read entirely — those would have already
 * been wrapped into `broker-unreachable`, but for downstream API
 * calls in pass-through mode the caller still sees raw fetch
 * errors).
 */
export class AuthError extends Error {
  readonly kind: AuthErrorKind;
  readonly status?: number;

  constructor(kind: AuthErrorKind, message: string, status?: number) {
    super(message);
    this.name = 'AuthError';
    this.kind = kind;
    if (status !== undefined) {
      this.status = status;
    }
  }
}

/**
 * Public surface returned from {@link createAuthClient}.
 */
export interface AuthClient {
  /**
   * Auth-aware drop-in replacement for `fetch`. Caller-provided
   * `init` is never mutated in place; a fresh `Headers` instance
   * is built per call.
   *
   * In pass-through mode (no `tokenEndpoint`) the call forwards
   * to `fetch` unchanged — no broker fetch, no `Authorization`
   * header.
   */
  authedFetch(
    input: RequestInfo | URL,
    init?: RequestInit
  ): Promise<Response>;

  /**
   * Drop the cached token. Test seam — also useful if the host
   * page detects (e.g. via a host-page event) that the Sitecore
   * session has been invalidated and wants to force a fresh
   * broker round-trip on the next call.
   */
  reset(): void;
}

export interface CreateAuthClientOptions {
  /**
   * Broker URL. Empty string disables the auth-aware behaviour and
   * forces pass-through mode (DevBypass / unconfigured host).
   *
   * Per AUTHENTICATION.md §10 the broker is on Sitecore CD; the
   * widget calls it with `credentials: 'include'` so the Sitecore
   * forms cookie is forwarded. The endpoint is reached via plain
   * GET.
   */
  readonly tokenEndpoint: string;

  /**
   * Widget `api-base` at auth-client construction time. When this is a
   * root-relative path prefix (e.g. `/api/mwi`), {@link tokenEndpoint}
   * is joined under it via {@link resolveTokenEndpoint}. Absolute
   * origins leave the broker path page-relative.
   */
  readonly apiBase?: string;

  /**
   * Host element used as the dispatch target for the bubbling
   * `mwi-assistant-auth-error` event. In production this is the
   * `<mwi-assistant>` custom element itself; in unit tests a
   * detached `HTMLElement` works fine.
   */
  readonly host: EventTarget;
}

interface CachedToken {
  readonly token: string;
  /**
   * Absolute wall-clock ms (per `Date.now()`) at which the cached
   * token MUST be treated as stale and a fresh one fetched.
   */
  readonly refreshAtMs: number;
}

/**
 * Lower bound on how much lead time we leave before a token's hard
 * expiry. Picked so that even a slow broker round-trip + a slow
 * downstream request can complete with the *current* token in hand,
 * and a brief broker outage doesn't immediately strand the user.
 */
const HARD_FLOOR_REFRESH_LEAD_MS = 60_000;

/**
 * Fraction of the token's lifetime to consume before pre-emptive
 * refresh. 0.8 (refresh at 80% elapsed) gives generous slack on the
 * 15-minute tokens AUTHENTICATION.md §5 expects, without re-hitting
 * the broker so often that we lose the cache benefit.
 */
const FRACTION_OF_LIFETIME_BEFORE_REFRESH = 0.8;

/**
 * Defensive floor on `expires_in`. Anything shorter than a second
 * is unusable in practice — the time it takes to round-trip the
 * downstream request would itself exceed the lifetime — so we
 * surface it as `broker-rejected` rather than silently caching a
 * dead token.
 */
const MIN_LIFETIME_MS = 1_000;

function isValidBrokerResponse(value: unknown): value is BrokerTokenResponse {
  if (value === null || typeof value !== 'object') {
    return false;
  }
  const obj = value as Record<string, unknown>;
  return (
    typeof obj.access_token === 'string' &&
    obj.access_token.length > 0 &&
    typeof obj.expires_in === 'number' &&
    Number.isFinite(obj.expires_in) &&
    obj.expires_in > 0 &&
    typeof obj.token_type === 'string'
  );
}

/**
 * Build a fresh {@link Headers} instance from a caller-provided
 * `init.headers` of any shape (`Headers`, `Record<string, string>`,
 * `[string, string][]`, or `undefined`). The `Headers` constructor
 * naturally accepts every shape and produces a copy, which is
 * exactly the immutability guarantee the spec requires: writes to
 * the returned instance can never reach back into the caller's
 * data.
 */
function cloneHeadersWithBearer(
  token: string,
  init: RequestInit | undefined
): Headers {
  const headers = new Headers(init?.headers ?? undefined);
  headers.set('Authorization', `Bearer ${token}`);
  return headers;
}

/**
 * Builds an event detail without an `undefined` status field —
 * required because the widget's `tsconfig` enables
 * `exactOptionalPropertyTypes`, which forbids assigning
 * `{ status: undefined }` to an `{ status?: number }` slot.
 */
function buildEventDetail(
  kind: AuthErrorKind,
  status: number | undefined
): MwiAssistantAuthErrorEventDetail {
  return status === undefined ? { kind } : { kind, status };
}

export function createAuthClient(opts: CreateAuthClientOptions): AuthClient {
  /* In-memory only. AUTHENTICATION.md §13: "tokens in memory only;
     short lifetime; refresh by re-asking the broker." This closure
     never touches storage — there is no `localStorage`,
     `sessionStorage`, `document.cookie`, or `IndexedDB` reachable
     from this module. */
  let cached: CachedToken | null = null;

  /* Concurrency-coalescing seam: when a second caller arrives
     while the first broker request is still in flight, both share
     the same promise rather than racing two broker hits. Cleared
     in the `.finally(...)` of the inflight request so the next
     stale-cache call can start fresh. */
  let inflight: Promise<string> | null = null;

  function dispatchAuthError(
    kind: AuthErrorKind,
    status: number | undefined
  ): void {
    opts.host.dispatchEvent(
      new CustomEvent<MwiAssistantAuthErrorEventDetail>(
        MWI_ASSISTANT_AUTH_ERROR_EVENT,
        {
          bubbles: true,
          composed: true,
          detail: buildEventDetail(kind, status),
        }
      )
    );
  }

  async function fetchTokenInternal(): Promise<string> {
    const brokerUrl = resolveTokenEndpoint(opts.apiBase ?? '', opts.tokenEndpoint);
    let response: Response;
    try {
      response = await fetch(brokerUrl, {
        method: 'GET',
        /* AUTHENTICATION.md §10: forward the Sitecore forms cookie
           so the broker can validate the user's existing storefront
           session before brokering a JWT. */
        credentials: 'include',
        headers: { Accept: 'application/json' },
      });
    } catch {
      /* `fetch` rejects on transport-level failures (DNS, CORS,
         TLS, abort). The cause object can carry diagnostic info,
         but per AUTHENTICATION.md §13 we deliberately do not
         re-emit it — error messages must not include the token,
         and we'd rather under-share than risk leaking host-page
         context through a chained Error. */
      dispatchAuthError('broker-unreachable', undefined);
      throw new AuthError('broker-unreachable', 'Token broker unreachable');
    }

    if (!response.ok) {
      const status = response.status;
      dispatchAuthError('broker-rejected', status);
      throw new AuthError(
        'broker-rejected',
        `Token broker returned status ${status}`,
        status
      );
    }

    let body: unknown;
    try {
      body = await response.json();
    } catch {
      dispatchAuthError('broker-rejected', response.status);
      throw new AuthError(
        'broker-rejected',
        'Token broker returned malformed JSON',
        response.status
      );
    }

    if (!isValidBrokerResponse(body)) {
      dispatchAuthError('broker-rejected', response.status);
      throw new AuthError(
        'broker-rejected',
        'Token broker response did not match the documented shape',
        response.status
      );
    }

    const lifetimeMs = body.expires_in * 1000;
    if (lifetimeMs < MIN_LIFETIME_MS) {
      dispatchAuthError('broker-rejected', response.status);
      throw new AuthError(
        'broker-rejected',
        'Token broker returned an unusably short lifetime',
        response.status
      );
    }

    /* Refresh policy (AUTHENTICATION.md §11): refresh when EITHER
       80% of `expires_in` has elapsed OR the remaining lifetime
       drops below 60s — whichever fires first. The earlier of the
       two is the more conservative choice: it leaves headroom for
       a slow broker round-trip on the refresh, so a brief broker
       hiccup can't strand the user with an expired token mid-turn.
       The 60s floor specifically defends against unusually short
       `expires_in` values where a flat 80% of-lifetime would buy
       almost no safety. */
    const lead = Math.max(
      HARD_FLOOR_REFRESH_LEAD_MS,
      lifetimeMs * (1 - FRACTION_OF_LIFETIME_BEFORE_REFRESH)
    );
    const refreshAtMs = Date.now() + Math.max(0, lifetimeMs - lead);

    cached = { token: body.access_token, refreshAtMs };
    return body.access_token;
  }

  function getValidToken(): Promise<string> {
    if (cached !== null && Date.now() < cached.refreshAtMs) {
      return Promise.resolve(cached.token);
    }
    if (inflight !== null) {
      return inflight;
    }
    inflight = fetchTokenInternal().finally(() => {
      inflight = null;
    });
    return inflight;
  }

  async function attempt(
    input: RequestInfo | URL,
    init: RequestInit | undefined,
    retryOn401: boolean
  ): Promise<Response> {
    const token = await getValidToken();
    const headers = cloneHeadersWithBearer(token, init);
    /* Spread first, then overwrite headers: the spread carries
       method/body/credentials/etc. unchanged, and the explicit
       headers field replaces whatever the caller passed (which
       has already been *copied* into `headers`, so this also
       satisfies the "do not mutate caller-supplied
       Headers/object in place" requirement). */
    const augmentedInit: RequestInit = { ...(init ?? {}), headers };
    const response = await fetch(input, augmentedInit);

    if (response.status === 401) {
      /* Reactive refresh (AUTHENTICATION.md §11): on the first
         401, drop the cached token, fetch a fresh one, retry the
         original request exactly once. The retry passes
         `retryOn401: false` so a second 401 short-circuits to an
         AuthError. Method, body, and any caller-supplied headers
         are preserved by re-using the same `init` reference; the
         retry rebuilds its own Authorization header off the new
         token. */
      cached = null;
      if (retryOn401) {
        return attempt(input, init, false);
      }
      dispatchAuthError('unauthorized', 401);
      throw new AuthError(
        'unauthorized',
        'Downstream API rejected refreshed token',
        401
      );
    }
    return response;
  }

  function authedFetch(
    input: RequestInfo | URL,
    init?: RequestInit
  ): Promise<Response> {
    if (opts.tokenEndpoint === '') {
      /* Pass-through mode: the local Assistant API uses
         `Authentication:DevBypass:Enabled = true` in
         `appsettings.Development.json` so it requires no Bearer.
         We forward the caller's input/init verbatim — no broker
         fetch, no header injection, no auth event dispatch
         (pass-through is a configured mode, not a failure). */
      return fetch(input, init);
    }
    return attempt(input, init, true);
  }

  function reset(): void {
    cached = null;
    inflight = null;
  }

  return { authedFetch, reset };
}
